import { RefreshCw, Sparkles } from 'lucide-react';
import { threadAnalysisNotice } from '../../lib/clinic-logic';
import { cn } from '../../lib/utils';
import type { EmailThreadMetadata } from '../../types/email';
import { isRetryableAnalysisReason } from '../../types/email';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';

export interface AnalysisNoticeProps {
  thread: EmailThreadMetadata;
  /**
   * Re-run analysis for this one thread. Wire to `clinicApi.ingestThread`. Omit for
   * read-only surfaces; the notice still renders, just without the affordance.
   */
  onRetry?: (threadId: string) => void;
  isRetrying?: boolean;
  className?: string;
}

/**
 * Why a thread carries no summary, plus a retry where retrying could actually help.
 *
 * Ingest and analysis are separate phases (ADR-0003), so an unanalysed thread is a
 * normal, expected state rather than an error — it still has its subject, sender,
 * labels and tasks. What it must never be is a *silently* blank row: a user looking
 * at an empty title with no explanation learns nothing and assumes the app is
 * broken. Retry is offered only for the reasons that are actually transient;
 * `disabled` and `before_start_date` are configuration, and re-running them would
 * rebuild the cost problem those settings exist to avoid.
 */
export function AnalysisNotice({ thread, onRetry, isRetrying, className }: AnalysisNoticeProps) {
  const notice = threadAnalysisNotice(thread);
  if (!notice) return null;

  const canRetry = Boolean(onRetry) && isRetryableAnalysisReason(thread.analysisReason);

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Badge
        variant="outline"
        className="h-5 gap-1 border-border bg-muted px-1.5 text-[10px] font-medium text-foreground"
      >
        <Sparkles className="h-2.5 w-2.5 shrink-0" />
        {notice}
      </Badge>
      {canRetry && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isRetrying}
          onClick={() => onRetry?.(thread.threadId)}
          className="h-5 gap-1 px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className={cn('h-2.5 w-2.5', isRetrying && 'animate-spin')} />
          {isRetrying ? 'Analyzing…' : 'Retry'}
        </Button>
      )}
    </div>
  );
}
